import React, { memo, useRef } from 'react';
import { Button } from 'appkit-react';

import { CardWithLayout } from '@modules/insights/card';
import InsightModal from '../analysis/insightModal';
import { insightModals, INSIGHT_FIELDS, USER_PERMISSION_MODEL } from '@model';
import AccessIndicator from '@components/accessIndicator';

const { G_PRINCIPLE_DETAIL, INSIGHT_FORM } = insightModals;

export const getToggleModalByRef =
  (_modelRef) => (type, currentDetail) => () => {
    if (!_modelRef?.current) {
      return;
    }
    _modelRef.current.show({
      type,
      currentDetail,
      afterDelete: () => {
        if (!_modelRef.current) {
          return;
        }
        _modelRef.current.close();
      },
    });
  };

/** True when Draft JSON or plain text has no visible characters. */
export const isRichContentEmpty = (value) => {
  if (value == null || value === '') {
    return true;
  }

  if (typeof value === 'object' && value.blocks) {
    const text = value.blocks
      .map((block) => block?.text || '')
      .join('')
      .replace(/\u200B/g, '')
      .trim();
    return text.length === 0;
  }

  if (typeof value !== 'string') {
    return true;
  }

  const trimmed = value.trim();
  if (trimmed === '') {
    return true;
  }

  if (!trimmed.startsWith('{')) {
    return false;
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed?.blocks) {
      return isRichContentEmpty(parsed);
    }
  } catch {
    return false;
  }

  return true;
};

export const hasDisplayablePrincipleContent = (principle) =>
  !(
    isRichContentEmpty(principle?.title) &&
    isRichContentEmpty(principle?.guidingPrinciple)
  );

export const filterDisplayablePrinciples = (principles = []) =>
  principles.filter(hasDisplayablePrincipleContent);

export const formatCardData = (d, _toggle) =>
  d.map((e) => ({
    id: e.id,
    title: e.title,
    content: e.guidingPrinciple,
    action: {
      title: 'View Detail',
      clickHandler: _toggle(G_PRINCIPLE_DETAIL, {
        ...e,
        insightId: e.id,
      }),
    },
  }));

const GuidingPrincipleCards = ({ data }) => {
  const modalRef = useRef(null);
  const principles = [...data];

  const toggleModal = getToggleModalByRef(modalRef);

  const principlesCards = formatCardData(principles, toggleModal);

  return (
    <>
      <InsightModal
        ref={modalRef}
        rootNode=".app-content"
        field={INSIGHT_FIELDS.PRINCIPLE}
        shouldShowGoBack={false}
        disableScroll
      />
      <div className="subtitle-container">
        <div className="subtitle">
          <span>Guiding Principles</span>
          <AccessIndicator
            page={USER_PERMISSION_MODEL.INSIGHT.guidingPrinciples}
          />
        </div>
        <Button
          size="lg"
          className="add-principle-button role-control"
          onClick={toggleModal(INSIGHT_FORM)}
        >
          <span className="appkiticon icon-circle-plus-fill a-font-16" />
          GUIDING PRINCIPLE
        </Button>
      </div>
      <div className="insights-cards">
        <CardWithLayout
          cardsData={principlesCards}
          animActive
          collection="gd"
        />
      </div>
    </>
  );
};

export default memo(GuidingPrincipleCards);
